<template>
  <div
    v-if="state"
    ref="productMediaReferenceCarousel"
    :data-carousel="isCarousel"
    class="product-media"
  >
    <div class="product-media-wrapper">
      <div class="product-media-images">
        <template v-if="carouselItems.length">
          <div
            v-for="(carouselItem, i) in carouselItems"
            :key="`carousel-image-${i}`"
            :class="{
              'responsive-image ratio ratio-1': true,
              'active': i === currentCarouselItemIndex
            }"
          >
            <img
              v-if="carouselItem.type === 'image'"
              :src="carouselItem.value?.src"
              :alt="carouselItem.value?.alt"
              width="450"
              height="450"
            />
            <div
              v-if="carouselItem.type === 'sirvSpin'"
              :class="{
                'responsive-image ratio ratio-1': true,
                'active': i === currentCarouselItemIndex
              }"
            >
              <div class="Sirv" :data-src="carouselItem.value" style="position: absolute;" />
            </div>
            <div v-if="carouselItem.type === 'video'">
              <button
                :data-toggle="carouselItem.type === 'video' ? 'modal' : null"
                :data-target="carouselItem.type === 'video' ? `#${carouselItem.value?.id}VideoModal` : null"
                class="product-media-video-button"
                type="button"
              >
                <img
                  v-if="carouselItem.type === 'video'"
                  :src="carouselItem.value?.thumb"
                  :alt="carouselItem.value?.title"
                  class="no-pointer"
                  width="450"
                  height="450"
                />
                <SloIcon name="CirclePlaySolid" size="lg" />
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="responsive-image ratio ratio-1 active">
            <img
              src="/themes/sloan/img/product-placeholder.png"
              :alt="'Sloan Product Placeholder Image' | t"
              width="450"
              height="450"
            />
          </div>
        </template>
      </div>
    </div>
    <div v-if="showThumbs">
      <div
        class="product-media-thumbs-wrapper"
        :style="isSirvSpinActive ? '--blur-width: 0px;' : '--blur-width: 12px;'"
      >
        <div class="product-media-thumbs">
          <button
            v-for="(carouselItem, i) in carouselItems"
            :key="`carousel-image-thumb-${i}`"
            :class="{
              'responsive-image': true,
              'active': i === currentCarouselItemIndex,
              'sirv-image-button': carouselItem.type === 'sirvSpin'
            }"
            @click="handleCarouselItemClick(i, carouselItem)"
            :data-toggle="carouselItem.type === 'video' ? 'modal' : null"
            :data-target="carouselItem.type === 'video' ? `#${carouselItem.value?.id}VideoModal` : null"
            type="button"
          >
            <img
              v-if="carouselItem.type === 'image'"
              :src="carouselItem.value?.src"
              :alt="carouselItem.value?.alt"
              class="no-pointer"
              width="250"
              height="250"
            />
            <SloIcon v-if="carouselItem.type === 'sirvSpin'" name="ThreeSixtySpin" size="lg" />
            <template v-if="carouselItem.type === 'video'">
              <img
                v-if="carouselItem.type === 'video'"
                :src="carouselItem.value?.thumb"
                :alt="carouselItem.value?.title"
                class="no-pointer"
                width="250"
                height="250"
              />
              <SloIcon name="CirclePlaySolid" size="lg" />
            </template>
          </button>
        </div>
      </div>
    </div>
    <div v-if="isCarousel" class="product-media-carousel-dots">
      <button
        v-for="(carouselItem, i) in carouselItems"
        :key="`carousel-dot-${i}`"
        @click="handleCarouselItemClick(i, carouselItem)"
        :class="{
          'product-media-carousel-dot': true,
          'is-selected': i === currentCarouselItemIndex
        }"
        :aria-label="'Scroll to slide' | t"
        type="button"
      >
        <span class="sr-only">{{ 'Scroll to slide' | t }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import EmblaCarousel from 'embla-carousel';
import ClassNames from 'embla-carousel-class-names';
import Fade from 'embla-carousel-fade';
import {createWidgetMixin} from 'vue-instantsearch';
import {connectHits} from 'instantsearch.js/es/connectors';
import t from '../../main/i18n';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  name: 'ProductMedia',
  components: {
    SloIcon
  },
  filters: {
    t
  },
  mixins: [createWidgetMixin({connector: connectHits})],
  props: {
    images: {type: Array, default: () => []},
    videos: {type: Array, default: () => []},
    sirvSpin: {type: String, default: ''}
  },
  data() {
    return {
      carouselItems: [],
      currentCarouselItemIndex: 0,
      isSirvSpinActive: false,
      embla: null,
      emblaThumbs: null
    };
  },
  computed: {
    isCarousel() {
      return this.carouselItems.length > 4;
    },
    showThumbs() {
      return this.carouselItems.length > 1;
    },
    singleResult() {
      return this.state.results.nbHits === 1 ? this.state.hits[0] : false;
    },
    currentFinishRefinement() {
      return this.state.results._state.facetsRefinements?.variation_finish?.[0];
    },
    currentImageIndex() {
      return this.carouselItems
        .findIndex(item => item.value?.finish_name === this.currentFinishRefinement) || 0;
    }
  },
  watch: {
    state() {
      let items = [];

      if (this.singleResult && this.singleResult?.variation_image) {
        items = JSON.parse(this.singleResult.variation_image).map(image => ({type: 'image', value: image}));
      } else {
        items = this.images.map(image => ({type: 'image', value: image}));
      }

      let newItems = items.slice(0, 1);

      if (this.sirvSpin) {
        newItems = newItems.concat([{type: 'sirvSpin', value: this.sirvSpin}]);
      }

      if (this.videos.length) {
        newItems = newItems.concat(
          this.videos.map(video => ({type: 'video', value: video}))
        );
      }

      this.carouselItems = newItems.concat(items.slice(1));
      setTimeout(() => this.initEmblaCarousel(), 300);
    }
  },
  methods: {
    handleCarouselItemClick(index, item) {
      this.isSirvSpinActive = item.type === 'sirvSpin';
      this.currentCarouselItemIndex = index;
      this.embla?.scrollTo(index, true);
    },
    initEmblaCarousel() {
      if (this.isCarousel && this.$refs.productMediaReferenceCarousel) {
        const viewportMain = this.$refs.productMediaReferenceCarousel.querySelector('.product-media-wrapper');
        const viewportThumbs = this.$refs.productMediaReferenceCarousel.querySelector('.product-media-thumbs-wrapper');

        this.embla = EmblaCarousel(
          viewportMain,
          {watchDrag: false},
          [ClassNames({snapped: 'active'}), Fade()]
        );

        this.emblaThumbs = EmblaCarousel(
          viewportThumbs,
          {align: 'start', containScroll: 'keepSnaps', dragFree: true},
          [ClassNames({draggable: 'cursor-grab', dragging: 'cursor-grabbing'})]
        );

        this.emblaScrollSnaps = this.embla.scrollSnapList();

        if (this.emblaThumbs) {
          const addToggleThumbBtnsActive = (emblaApiMain, emblaApiThumb) => {
            const slidesThumbs = emblaApiThumb.slideNodes();

            const toggleThumbBtnsState = () => {
              emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap(), true);
              const previous = emblaApiMain.previousScrollSnap();
              const selected = emblaApiMain.selectedScrollSnap();
              slidesThumbs[previous].classList.remove('active');
              slidesThumbs[selected].classList.add('active');
            };

            emblaApiMain.on('select', toggleThumbBtnsState);
            emblaApiThumb.on('init', toggleThumbBtnsState);

            return () => {
              const selected = emblaApiMain.selectedScrollSnap();
              slidesThumbs[selected].classList.remove('active');
            };
          };

          const removeToggleThumbBtnsActive = addToggleThumbBtnsActive(this.embla, this.emblaThumbs);

          this.embla.on('destroy', removeToggleThumbBtnsActive);
          this.emblaThumbs.on('destroy', removeToggleThumbBtnsActive);

          this.handleCarouselItemClick(this.currentImageIndex, this.carouselItems[0]);
        }
      }
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.product-media {
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &-images {
    display: grid;
  }
  &-images > * {
    grid-area: 1 / -1;
    transition: opacity 0.3s;
  }
  &-images > *:not(.active) {
    opacity: 0;
    pointer-events: none;
  }

  &-thumbs-wrapper {
    overflow: hidden;
  }
  &-thumbs {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1em;
    margin-top: 1em;
  }
  &-thumbs button {
    // calculate thumb width, accounting for gaps
    flex-basis: calc(20% - (1em * 4 / 5));
    position: relative;
    padding: 0;
    background: transparent;
  }
  &-thumbs button:nth-child(n + 6):last-child {
    // left-align all thumbs once we've wrapped at --count + 1
    margin-right: auto;
  }
  &-thumbs button:before {
    content: '';
    display: block;
    padding-top: 100%;
  }
  &-thumbs button:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    left: 0;
    bottom: 0;
    background-color: $color-blue-400;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &-thumbs button.active {
    &:after {
      opacity: 1;
    }
  }
  &-thumbs .slo-icon {
    color: $color-blue-400;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) scale(1.625);
  }
  &-thumbs .sirv-image-button {
    background-color: #fafafa;
    border-radius: 0;
  }
  &-video-button {
    color: $color-blue-400;

    .slo-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
      width: 25%;
      height: 25%;

      svg {
        width: 100%;
        height: 100%;
      }
    }
  }
}

[data-carousel='true'] {
  .product-media {
    &-wrapper {
      overflow: hidden;
    }

    &-images {
      position: relative;
      display: flex;
    }
    &-images > * {
      grid-column: initial;
      flex: 1;
      min-width: 100%;
    }

    &-thumbs {
      justify-content: start;
      flex-wrap: nowrap;
    }

    &-thumbs button {
      flex: 1 0 20%;
    }

    &-carousel-dots {
      display: flex;
      justify-content: center;
      gap: 0.5em;
      margin-top: 1em;
    }

    &-carousel-dot {
      width: 0.5em;
      height: 0.5em;
      padding: 0;
      border-radius: 9999px;
      background-color: $color-gray-300;
      cursor: pointer;
      transition: background-color 0.3s;

      &.is-selected {
        background-color: $color-blue-400;
      }
    }
  }

  .product-media-thumbs-wrapper {
    --blur-width: 12px;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: var(--blur-width);
      background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff);
      transition: width 0.3s ease;
    }
  }
}

[data-carousel='true'].product-media {
  @media (min-width: 768px) {
    max-width: 226px;
  }
  @media (min-width: 798px) {
    max-width: none;
  }
}
</style>
